import type { TextRenderInput } from '../types';

/**
 * 判断输入能否安全进入文字引擎。
 *
 * @param input - 待校验的文字渲染输入。
 * @returns 输入满足普通文字排版约束时返回 true。
 */
export const isValidInput = (input: TextRenderInput): boolean => {
    if (!Number.isFinite(input.fontSize) || input.fontSize <= 0) return false;
    if (!Number.isFinite(input.lineHeightScale) || input.lineHeightScale <= 0) return false;
    if (!Number.isFinite(input.letterSpacing)) return false;

    const inlineSize = input.writingMode === 'horizontal-tb' ? input.width : input.height;
    return inlineSize === null || (Number.isFinite(inlineSize) && inlineSize > 0);
};

/**
 * 创建使用当前可用字体族的运行时 Text Render Input。
 *
 * TextRenderInput 保存宿主声明的字体族，而字体 subset 注册到文字引擎后可能使用另一字体族名称。
 *
 * 例如图层声明 `fontFamily: 'AlibabaPuHuiTi-Regular'`，FontLoader 可能映射到引擎注册的 `'AlibabaPuHuiTi-Regular-c3d061bf07e760d9d445b97680f899c8'`；
 * 派生 input 使用 `'AlibabaPuHuiTi-Regular-c3d061bf07e760d9d445b97680f899c8'` 排版和绘制，而原 input 继续保留 `'AlibabaPuHuiTi-Regular'`。
 *
 * 本函数复制输入，并统一替换顶层文字、富文本 spans 和 SVG marker 编号字体，确保布局缓存、文字引擎与
 * SVG pipeline 使用同一份字体解析结果。原输入保持不变，后续字体请求仍以宿主声明的字体族为准。
 *
 * @param input - 使用宿主声明字体族的原始输入。
 * @param fontFamilies - 声明字体族到当前可用字体族（原字体族或已注册 subset）的映射。
 * @returns 使用实际字体族、可供布局与绘制共同消费的派生输入。
 */
export const resolveInputFonts = (
    input: TextRenderInput,
    fontFamilies: ReadonlyMap<string, string>,
): TextRenderInput => {
    const resolvedInput: TextRenderInput = {
        ...input,
        fontFamily: fontFamilies.get(input.fontFamily) ?? input.fontFamily,
        spans: input.spans.map((span) => ({
            ...span,
            fontFamily: fontFamilies.get(span.fontFamily) ?? span.fontFamily,
        })),
    };

    const marker = input.listMarker;
    if (marker?.kind === 'svg') {
        const { numberFontFamily } = marker;
        resolvedInput.listMarker = {
            ...marker,
            numberFontFamily: numberFontFamily ? (fontFamilies.get(numberFontFamily) ?? numberFontFamily) : undefined,
        };
    }

    return resolvedInput;
};
